/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdOnboarding — first-time-user enrolment form. Inserted by `TdSetup`
 * into the setup-header container when the authenticated user is not
 * yet a `Member`. Collects personal details + terms acceptance, POSTs
 * them to `/api/auth/register`, then advances the enclosing `td-setup`
 * to `setupSecurityPolicies`.
 *
 * Lit light-DOM component. The user's email lands in the disabled
 * input via Lit's `.value=` property binding (set as a JS property,
 * never via HTML interpolation) so a hostile JWT claim can't smuggle
 * markup through the template.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { tdFetch } from '../services/tdfetch.js';
import {
  authService,
  parseRegisterResponse,
  type RegisterResponse,
} from '../services/auth.js';
import type { SetupStateName } from './TdSetup.js';
import { t, LocaleController } from '../services/i18n.js';

/** Shape collected from the personal-data form before POST. */
interface OnboardingFormData {
  [field: string]: string;
}

@customElement('td-onboarding')
export class TdOnboarding extends LitElement {
  @state() private email = '';

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.email = authService.getEmail() ?? '';
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    console.log('TdOnboarding disconnected');
  }

  async register(formData: OnboardingFormData): Promise<RegisterResponse> {
    console.log('Registration data:', formData);
    const response = await tdFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
    if (!response.ok) {
      throw new Error('Registration failed');
    }
    // #183: parse via `parseRegisterResponse` so a doubled JWT (or any
    // other malformed token field) throws `AuthResponseError` at the
    // boundary rather than landing in localStorage. Defence in depth:
    // `setAccessToken` also re-checks the shape with its own guard.
    const data = parseRegisterResponse(await response.json());
    authService.setAccessToken(data.token);
    console.log('Registration response:', data);
    return data;
  }

  nextState(element: HTMLElement, stateName: SetupStateName): void {
    let ancestor: HTMLElement | null = element.parentElement;
    while (ancestor && ancestor.tagName !== 'TD-SETUP') {
      ancestor = ancestor.parentElement;
    }

    if (ancestor && ancestor.tagName === 'TD-SETUP') {
      (ancestor as HTMLElement & { state: SetupStateName }).state = stateName;
    }

    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  }

  private _handleSubmit(event: Event): void {
    event.preventDefault();
    const form = this.querySelector<HTMLFormElement>(
      '.personal-data-collection'
    );
    if (!form) return;

    const formData: OnboardingFormData = {};
    const inputs = form.querySelectorAll<HTMLInputElement>('wa-input');
    inputs.forEach(input => {
      // #89: harvest by the stable `name` attribute, not the visible
      // label — labels are localized now, and the POST payload keys
      // must not change with the UI language.
      const name = input.getAttribute('name');
      const value = input.value;
      if (name && value) {
        formData[name] = value;
      }
    });

    const checkbox = form.querySelector<HTMLInputElement>('wa-checkbox');
    if (checkbox && checkbox.checked) {
      formData['termsAccepted'] = new Date().toISOString();
    }

    // Fire-and-forget the registration; UX advances regardless.
    // The `.catch` keeps a rejected register() (e.g. 4xx/5xx from
    // /api/auth/register) from leaking as an unhandled promise
    // rejection. The failed registration was silent before either
    // way — only the console.error is new.
    this.register(formData).catch(err => {
      console.error('TdOnboarding: registration failed', err);
    });

    this.nextState(this, 'setupSecurityPolicies');
  }

  override render(): TemplateResult {
    return html`
      <div class="wa-cluster login-container">
        <div class="onboarding-header">
          <h2>${t('onboarding.welcome')}</h2>
          <p>${t('onboarding.intro1')}</p>
          <p>${t('onboarding.intro2')}</p>

          <wa-card id="personal-tab-panel" name="personal">
            <form
              class="personal-data-collection"
              @submit=${this._handleSubmit}
            >
              <wa-input
                type="email"
                name="email"
                label=${t('onboarding.emailLabel')}
                id="email-input"
                disabled
                .value=${this.email}
              ></wa-input>
              <br />
              <wa-input
                type="string"
                name="fullname"
                label=${t('onboarding.fullNameLabel')}
                placeholder=${t('onboarding.fullNamePlaceholder')}
                required
              ></wa-input>
              <br />
              <wa-input
                type="string"
                name="salutation"
                label=${t('onboarding.salutationLabel')}
                placeholder=${t('onboarding.salutationPlaceholder')}
                required
              ></wa-input>
              <br />
              <wa-input
                type="date"
                name="birthday"
                label=${t('onboarding.birthdayLabel')}
                placeholder="1990-01-01"
                required
              ></wa-input>
              <br />
              <wa-checkbox required>
                ${t('onboarding.termsAccept')}
                <a href="#" title=${t('onboarding.termsLink')}
                  >${t('onboarding.termsLink')}</a
                >
              </wa-checkbox>
              <br />
              <wa-button id="dismiss-btn" type="submit" variant="brand">
                ${t('onboarding.submit')}
              </wa-button>
            </form>
          </wa-card>
        </div>
      </div>
    `;
  }
}
